import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Grid
} from '@mui/material';
import {
  getAccountProspectSearchParams,
  searchAccountProspectsBackground,
  getAccountSearchJobStatus,
  getAccountSearchJobResult
} from '../../api/prospects';
import { PROSPECT_QUERY_LIMIT } from '../../types/prospects';
import { useSnackbar } from '../../context/SnackbarContext';
import { getAccountSearchFilterConfig } from '../../config/prospectSearchFilterConfig';
import type {
  FilterFieldDef,
  ProspectSearchFilterConfig
} from '../../types/prospectSearchFilters';
import type { AccountProspectResponse } from '../../types/prospects';

interface ProspectDatabaseSearchFormProps {
  onConfirm: (rows: Record<string, any>[]) => void;
  onCancel: () => void;
}

const buildQueryFromValues = (
  config: ProspectSearchFilterConfig,
  values: Record<string, string | string[]>
): Record<string, any> => {
  const query: Record<string, any> = {};

  for (const group of config.groups) {
    for (const field of group.fields) {
      const val = values[field.id];
      if (val === undefined || val === null) continue;

      if (field.type === 'text') {
        if (typeof val === 'string' && val.trim()) {
          if (field.splitBy) {
            query[field.apiKey] = val
              .split(field.splitBy)
              .map((s) => s.trim())
              .filter((s) => s.length > 0);
          } else {
            query[field.apiKey] = val.trim();
          }
        }
      } else if (field.type === 'date') {
        if (typeof val === 'string' && val.trim()) {
          query[field.apiKey] = new Date(val).toISOString();
        }
      } else if (field.type === 'multiselect') {
        const arr = Array.isArray(val) ? val : [];
        if (arr.length > 0) {
          query[field.apiKey] = arr;
        }
      }
    }
  }

  return query;
};

const hasAtLeastOneFilter = (
  config: ProspectSearchFilterConfig,
  values: Record<string, string | string[]>
): boolean => {
  for (const group of config.groups) {
    for (const field of group.fields) {
      const val = values[field.id];
      if (val === undefined || val === null) continue;
      if (typeof val === 'string' && val.trim()) return true;
      if (Array.isArray(val) && val.length > 0) return true;
    }
  }
  return false;
};

const validateRequired = (
  config: ProspectSearchFilterConfig,
  values: Record<string, string | string[]>
): string | null => {
  for (const group of config.groups) {
    for (const field of group.fields) {
      if (!field.required) continue;
      const val = values[field.id];
      const isEmpty =
        val === undefined ||
        val === null ||
        (typeof val === 'string' && !val.trim()) ||
        (Array.isArray(val) && val.length === 0);
      if (isEmpty) {
        return `${field.label} is required`;
      }
    }
  }
  if (config.requireAtLeastOneFilter && !hasAtLeastOneFilter(config, values)) {
    return 'Please fill in at least one filter';
  }
  return null;
};

const ProspectDatabaseSearchForm: React.FC<ProspectDatabaseSearchFormProps> = ({
  onConfirm,
  onCancel
}) => {
  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [limitInput, setLimitInput] = useState('10');
  const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(
    null
  );
  const [config, setConfig] = useState<ProspectSearchFilterConfig | null>(null);
  const [values, setValues] = useState<Record<string, string | string[]>>({});

  useEffect(() => {
    let isMounted = true;
    const fetchParams = async () => {
      try {
        const params = await getAccountProspectSearchParams();
        const cfg = getAccountSearchFilterConfig({
          last_fundraising_series: params.last_fundraising_series,
          industry: params.industry,
          country: params.country,
          size_range: params.size_range
        });
        if (isMounted) {
          setConfig(cfg);
          setValues({
            last_fundraising_date: '',
            last_fundraising_series: [],
            industries: [],
            countries: [],
            size_ranges: []
          });
        }
      } catch (err: any) {
        if (isMounted) {
          showSnackbar(
            err?.message ?? 'Failed to load search options',
            'error'
          );
          onCancel();
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchParams();
    return () => {
      isMounted = false;
    };
  }, [onCancel, showSnackbar]);

  const updateValue = (fieldId: string, value: string | string[]) => {
    setValues((prev) => ({ ...prev, [fieldId]: value }));
  };

  const resolvedSearchLimit = (): number => {
    const trimmed = limitInput.trim();
    if (trimmed === '') return 10;
    const n = parseInt(trimmed, 10);
    if (Number.isNaN(n)) return 10;
    return Math.min(Math.max(n, 1), PROSPECT_QUERY_LIMIT);
  };

  const handleSearch = async () => {
    if (!config) return;

    const err = validateRequired(config, values);
    if (err) {
      showSnackbar(err, 'error');
      return;
    }

    const query = buildQueryFromValues(config, values);
    const industries = (query.industries as string[]) ?? [];
    if (industries.length === 0) {
      showSnackbar('Please select at least one industry', 'error');
      return;
    }
    const fullQuery: Record<string, any> = {
      industries
    };
    if (query.last_fundraising_date) {
      fullQuery.last_fundraising_date = query.last_fundraising_date;
    }
    if (
      Array.isArray(query.last_fundraising_series) &&
      query.last_fundraising_series.length > 0
    ) {
      fullQuery.last_fundraising_series = query.last_fundraising_series;
    }
    if (Array.isArray(query.countries) && query.countries.length > 0) {
      fullQuery.countries = query.countries;
    }
    if (Array.isArray(query.size_ranges) && query.size_ranges.length > 0) {
      fullQuery.size_ranges = query.size_ranges;
    }
    setSearching(true);
    try {
      const jobResponse = await searchAccountProspectsBackground(
        fullQuery as {
          industries: string[];
          countries?: string[];
          size_ranges?: string[];
          last_fundraising_date?: string;
          last_fundraising_series?: string[];
        },
        resolvedSearchLimit()
      );
      startAccountPolling(jobResponse.job_id);
    } catch (err: any) {
      showSnackbar(err?.message ?? 'Failed to search prospects', 'error');
      setSearching(false);
    }
  };

  const startAccountPolling = (jobId: string) => {
    const interval = setInterval(async () => {
      try {
        const status = await getAccountSearchJobStatus(jobId);

        if (status.status === 'finished') {
          clearInterval(interval);
          setPollingInterval(null);
          const results = await getAccountSearchJobResult(jobId);
          if (!results || results.length === 0) {
            showSnackbar('No prospects found under search criteria', 'error');
            setSearching(false);
            return;
          }
          showSnackbar(`Found ${results.length} prospects!`, 'success');
          onConfirm(results as AccountProspectResponse[]);
          setSearching(false);
        } else if (status.status === 'failed') {
          clearInterval(interval);
          setPollingInterval(null);
          showSnackbar(
            `Search failed: ${status.error ?? 'Unknown error'}`,
            'error'
          );
          setSearching(false);
        }
      } catch (err: any) {
        clearInterval(interval);
        setPollingInterval(null);
        showSnackbar(
          `Failed to check status: ${err?.message ?? 'Unknown error'}`,
          'error'
        );
        setSearching(false);
      }
    }, 2000);
    setPollingInterval(interval);
  };

  useEffect(() => {
    return () => {
      if (pollingInterval) clearInterval(pollingInterval);
    };
  }, [pollingInterval]);

  const handleCancel = () => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
    setSearching(false);
    onCancel();
  };

  const renderField = (field: FilterFieldDef) => {
    const val = values[field.id];
    const fieldSx = (theme: any) => ({
      '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor:
          theme.customColors?.themePrimary ?? theme.palette.primary.main
      },
      '& .MuiInputLabel-root.Mui-focused': {
        color: theme.customColors?.themePrimary ?? theme.palette.primary.main
      }
    });

    if (field.type === 'text') {
      return (
        <TextField
          key={field.id}
          label={field.label}
          placeholder={field.placeholder}
          value={typeof val === 'string' ? val : ''}
          onChange={(e) => updateValue(field.id, e.target.value)}
          size="small"
          fullWidth
          sx={fieldSx}
        />
      );
    }

    if (field.type === 'date') {
      return (
        <TextField
          key={field.id}
          type="date"
          label={field.label}
          value={typeof val === 'string' ? val : ''}
          onChange={(e) => updateValue(field.id, e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
          fullWidth
          sx={fieldSx}
        />
      );
    }

    if (field.type === 'multiselect') {
      const arr = Array.isArray(val) ? val : [];
      const options = field.options ?? [];
      return (
        <Autocomplete
          key={field.id}
          multiple
          size="small"
          options={options}
          value={arr}
          onChange={(_, newValue) => updateValue(field.id, newValue)}
          getOptionLabel={(option) => option}
          renderInput={(params) => (
            <TextField
              {...params}
              label={field.label}
              placeholder={
                arr.length === 0 ? (field.placeholder ?? 'Type to search…') : ''
              }
            />
          )}
          ListboxProps={{
            sx: { maxHeight: 220 }
          }}
          limitTags={2}
          sx={fieldSx}
        />
      );
    }

    return null;
  };

  if (loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        py={4}
      >
        <CircularProgress size={28} />
        <Typography variant="body2" color="text.secondary" mt={1}>
          Loading options…
        </Typography>
      </Box>
    );
  }

  if (!config) {
    return null;
  }

  const allFields = config.groups.flatMap((g) => g.fields);
  const industriesField = allFields.find((f) => f.id === 'industries');
  const countriesField = allFields.find((f) => f.id === 'countries');
  const sizeRangesField = allFields.find((f) => f.id === 'size_ranges');
  const fundraisingFields = allFields.filter(
    (f) =>
      f.id === 'last_fundraising_date' || f.id === 'last_fundraising_series'
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box>{industriesField && renderField(industriesField)}</Box>
      {countriesField && <Box>{renderField(countriesField)}</Box>}
      {sizeRangesField && <Box>{renderField(sizeRangesField)}</Box>}
      {fundraisingFields.length > 0 && (
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 2, display: 'block' }}
          >
            Optional
          </Typography>
          <Grid container spacing={1.5}>
            {fundraisingFields.map((f) => (
              <Grid item xs={12} sm={6} key={f.id}>
                {renderField(f)}
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <TextField
          label="Max Results"
          type="number"
          value={limitInput}
          onChange={(e) => {
            const v = e.target.value;
            if (v === '') {
              setLimitInput('');
              return;
            }
            if (/^\d+$/.test(v)) {
              setLimitInput(v);
            }
          }}
          onBlur={() => {
            if (limitInput.trim() === '') return;
            const n = parseInt(limitInput, 10);
            if (Number.isNaN(n)) {
              setLimitInput('10');
              return;
            }
            setLimitInput(
              String(Math.min(Math.max(n, 1), PROSPECT_QUERY_LIMIT))
            );
          }}
          InputProps={{
            inputProps: { inputMode: 'numeric', pattern: '[0-9]*' }
          }}
          size="small"
          sx={{ width: 120 }}
          helperText={`Limit: ${PROSPECT_QUERY_LIMIT}`}
        />
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
        <Button
          onClick={handleCancel}
          disabled={searching}
          sx={(theme: any) => ({
            width: '50%',
            border: `1px solid ${theme.customColors?.themePrimary ?? theme.palette.primary.main}`,
            backgroundColor:
              theme.customColors?.themeSecondary ??
              theme.palette.background.paper,
            color:
              theme.customColors?.themePrimary ?? theme.palette.primary.main,
            borderRadius: '50px',
            textTransform: 'capitalize'
          })}
        >
          {searching ? 'Cancel' : 'Back'}
        </Button>
        <Button
          variant="contained"
          onClick={handleSearch}
          disabled={searching}
          sx={(theme: any) => ({
            width: '50%',
            border: `1px solid ${theme.customColors?.themePrimary ?? theme.palette.primary.main}`,
            backgroundColor:
              theme.customColors?.themePrimary ?? theme.palette.primary.main,
            color:
              theme.customColors?.themeSecondary ??
              theme.palette.primary.contrastText,
            borderRadius: '50px',
            textTransform: 'capitalize',
            '&:disabled': { borderColor: 'inherit' }
          })}
        >
          {searching ? 'Processing...' : 'Search & Select'}
        </Button>
      </Box>
    </Box>
  );
};

export default ProspectDatabaseSearchForm;
